import { PanelHeader } from '@app/components/base/content-structure/PanelHeader';
import { DownloadUrlButton } from '@app/core/media-player/DownloadUrlButton';
import { UrlMediaEmbed } from '@app/core/media-player/UrlMediaEmbed';
import { mimeType } from '@app/helpers/utils';
import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { defineMessage, FormattedMessage, MessageDescriptor } from 'react-intl';
import {
  buildAuditMediaFileUrl,
  resolveAuditMediaIdByCustomId,
} from '../../helpers/build-audit-media-file-url';
import { AuditMatchAudio } from '../../types';

const creativeTitleMessage = defineMessage({
  id: 'audit.verifyMatch.creativeTitle',
  defaultMessage: 'Media de referencia',
});

export interface VerifyMatchCreativePlayerProps {
  stationId: string;
  audio: AuditMatchAudio;
  /** Known media UUID; skips the customId lookup when provided. */
  mediaId?: string;
  /**
   * Direct media file URL. When set, skips api-v2 media resolution
   * (useful for Storybook demos).
   */
  mediaUrl?: string;
  /** Section header; defaults to “Media de referencia”. */
  title?: MessageDescriptor;
}

/**
 * Embeds the uploaded audit creative (reference file) inside the verify dialog.
 * Resolves media UUID from customId via api-v2 when `mediaId` is not passed, then plays/downloads the file.
 */
export function VerifyMatchCreativePlayer({
  stationId,
  audio,
  mediaId: mediaIdProp,
  mediaUrl,
  title = creativeTitleMessage,
}: VerifyMatchCreativePlayerProps) {
  const mediaQuery = useQuery({
    queryKey: ['audit-media-id', stationId, audio.customId],
    queryFn: () => resolveAuditMediaIdByCustomId(stationId, audio.customId),
    staleTime: 5 * 60 * 1000,
    enabled: !mediaIdProp && !mediaUrl,
  });

  const mediaId = mediaIdProp ?? mediaQuery.data;
  const url = mediaUrl ?? (mediaId ? buildAuditMediaFileUrl(mediaId) : undefined);
  const downloadUrl =
    mediaUrl ?? (mediaId ? buildAuditMediaFileUrl(mediaId, { download: true }) : undefined);
  const type = mimeType(audio.filename);

  return (
    <Box>
      <PanelHeader
        title={<FormattedMessage {...title} />}
        divider={false}
        actions={
          downloadUrl ? (
            <DownloadUrlButton url={downloadUrl} size="small" color="primary" iconOnly />
          ) : undefined
        }
      />
      {url ? <UrlMediaEmbed url={url} type={type} /> : null}
    </Box>
  );
}
